import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../../api/api';
import useAdminDraftGuard from '../useAdminDraftGuard';

const initial = { titleKo: '', bodyKo: '', titleEn: '', bodyEn: '', link: '' };

export default function ManualEventPushManagement() {
  const [form, setForm] = useState(initial);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const draft = useAdminDraftGuard(form, { busy });
  const valid = ['titleKo', 'bodyKo', 'link'].every((key) => form[key].trim()) && confirmed;
  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setConfirmed(false);
    setResult('');
    setError('');
  };
  const send = async (testOnly) => {
    if (!valid || busy) return;
    if (
      !window.confirm(
        testOnly
          ? '내 계정의 동의한 기기에 테스트 발송할까요?'
          : '이벤트·혜택 및 광고에 별도로 동의한 대상에게 지금 발송할까요? 발송 후 취소할 수 없습니다.',
      )
    )
      return;
    setBusy(true);
    setError('');
    setResult('');
    try {
      const response = await api.postAuth('/admin/app/event-push/send', {
        ...form,
        testOnly,
        confirmed: true,
      });
      const count = response.data.data.acceptedDevices;
      if (!testOnly) draft.markClean();
      setResult(
        `${testOnly ? '내 계정 테스트' : '수동 발송'}: 전송 접수 ${count}대. 실제 도착 수와는 다릅니다. 0대라면 수신 설정·등록 기기·중복·최근 발송 제한을 확인하세요.`,
      );
    } catch (e) {
      setError(
        e.message ||
          '발송 상태를 확인할 수 없습니다. 동일한 내용으로 재시도하면 중복 전송을 방지합니다.',
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <Stack spacing={2}>
      <Typography variant="h6">이벤트·광고 푸시 수동 발송</Typography>
      <Alert severity="info">
        자동 발송하지 않습니다. 앱 전체 알림과 ‘이벤트·혜택 및 광고’에 별도로 동의한 회원·비회원
        기기만 대상입니다. 한국 시간 09~20시에 발송하며, 실제 대상에게 최근 7일 내 다른 수동
        캠페인을 보냈다면 제외합니다. 동일 문구·링크는 재발송하지 않습니다.
      </Alert>
      <Alert severity="warning">
        먼저 내 계정으로 테스트하세요. 이벤트·광고는 앱에서 열리는 이벤트/공지 상세에 안내하고, 그
        상세에서 외부 신청 페이지로 연결하세요. 권한 안내와 별개이며 수신 동의를 자동으로 켜지
        않습니다.
      </Alert>
      <Alert severity="info">
        연결 경로 찾기: 웹의 ‘이벤트 및 공지사항’에서 연결할 게시물을 열고, 주소에서 도메인 뒤의
        /notice/슬러그 부분을 복사하세요. 후원 안내는 아래 ‘후원 화면 연결’ 버튼으로 지정합니다.
        앨범·리뷰 화면이나 외부 주소는 직접 지원하지 않습니다. 영어 제목·본문은 각각 선택이며,
        비우면 해당 한국어 문구로 발송됩니다.
      </Alert>
      {error && <Alert severity="error">{error}</Alert>}
      {result && <Alert severity="success">{result}</Alert>}
      <Box component="fieldset" disabled={busy} sx={{ border: 0, p: 0, m: 0 }}>
        <Stack spacing={2}>
          <Button variant="outlined" onClick={() => update('link', '/sponsorship')}>
            후원 화면 연결
          </Button>
          {form.link.trim() === '/sponsorship' && (
            <Alert severity="info">
              후원 화면 직접 이동은 이 기능이 포함된 새 앱에서 지원합니다. 실제 발송 전 업데이트된
              앱에서 내 계정 테스트로 확인하세요. 후원 홍보도 광고 수신 동의·빈도 제한을 동일하게
              적용합니다.
            </Alert>
          )}
          {[
            ['titleKo', '한국어 제목', 60],
            ['bodyKo', '한국어 본문', 180],
            ['titleEn', '영어 제목 (선택)', 60],
            ['bodyEn', '영어 본문 (선택)', 180],
            ['link', '앱 연결 경로', 208],
          ].map(([key, label, maxLength]) => (
            <TextField
              key={key}
              label={label}
              value={form[key]}
              onChange={(e) => update(key, e.target.value)}
              inputProps={{ maxLength }}
              fullWidth
              required={!['titleEn', 'bodyEn'].includes(key)}
              helperText={
                key === 'link'
                  ? '후원: /sponsorship · 이벤트/공지: /notice/이벤트-슬러그'
                  : undefined
              }
            />
          ))}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2">실제 푸시 문구 미리보기</Typography>
            <Typography
              sx={{ overflowWrap: 'anywhere' }}
            >{`(광고) 포스트팝 · ${form.titleKo}`}</Typography>
            <Typography sx={{ overflowWrap: 'anywhere' }}>{form.bodyKo}</Typography>
            <Typography variant="caption">
              수신 거부: 설정 &gt; 푸시 알림 &gt; 이벤트·혜택 및 광고
            </Typography>
            <Typography
              sx={{ mt: 2, overflowWrap: 'anywhere' }}
            >{`(Ad) Postpop · ${form.titleEn.trim() || form.titleKo.trim()}`}</Typography>
            <Typography sx={{ overflowWrap: 'anywhere' }}>
              {form.bodyEn.trim() || form.bodyKo.trim()}
            </Typography>
            <Typography variant="caption">
              Opt out: Settings &gt; Push notifications &gt; Events, offers &amp; ads
            </Typography>
          </Paper>
          <FormControlLabel
            control={
              <Checkbox checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
            }
            label="문구·연결 경로·수신 동의 대상과 발송 시간을 확인했습니다."
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="outlined" disabled={!valid || busy} onClick={() => send(true)}>
              내 계정 테스트
            </Button>
            <Button variant="contained" disabled={!valid || busy} onClick={() => send(false)}>
              동의 대상에게 발송
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
}
